import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gauge } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* Set Goals → Stage limits: business days per stage + the near-limit threshold (saved in the database). */
export default function StageLimitsSection({ authToken, isAdmin }) {
  const [data, setData] = useState(null);
  const H = { Authorization: `Bearer ${authToken}` };

  useEffect(() => {
    if (!authToken) return;
    axios.get(`${API}/api/gameplan/stage-limits`, { headers: H }).then((r) => setData(r.data)).catch(() => {});
  }, [authToken]); // eslint-disable-line

  if (!data) return null;
  const canEdit = isAdmin || data.can_manage;

  const save = async (patch) => {
    try {
      const r = await axios.put(`${API}/api/gameplan/stage-limits`, patch, { headers: H });
      setData(r.data);
      toast.success('Stage limits saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save stage limits'); }
  };
  const input = (key, value, onSave, step = 1) => (
    <input type="number" min={0} step={step} defaultValue={value ?? ''} key={`${key}-${value}`} disabled={!canEdit} placeholder="—"
      className="w-20 h-9 rounded-lg border border-slate-200 px-2 text-sm text-right tabular-nums disabled:opacity-60"
      onBlur={(e) => { const raw = e.target.value.trim(); const v = raw === '' ? null : parseFloat(raw); if (raw !== '' && !isFinite(v)) return; if (v !== (value ?? null)) onSave(v); }}
      data-testid={`stage-limit-${key}`} />
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-4" data-testid="stage-limits-section">
      <div className="flex items-start gap-3">
        <div className="cp-dark w-9 h-9 rounded-xl bg-[#171B21] text-white grid place-items-center flex-shrink-0"><Gauge className="w-4 h-4" /></div>
        <div>
          <h3 className="text-sm font-bold text-slate-900">Stage limits</h3>
          <p className="text-xs text-slate-500 mt-0.5">How many business days a file may sit in each stage before Pipeline health marks it over its limit. Leave a stage blank for no limit. Business days exclude weekends and the SLA holiday calendar.</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-sm">
        {data.stages.map((s) => (
          <label key={s.key} className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-slate-600">{s.label}</span>
            <span className="flex items-center gap-2">{input(s.key, data.limits[s.key], (v) => save({ limits: { [s.key]: v } }))}<span className="text-xs text-slate-500">bd</span></span>
          </label>
        ))}
      </div>
      <label className="flex items-center justify-between gap-3 text-sm">
        <span><span className="font-semibold text-slate-700">Near-limit threshold</span><span className="block text-xs text-slate-500">A file turns amber once it has used this share of its stage limit.</span></span>
        <span className="flex items-center gap-2">{input('near_pct', data.near_pct, (v) => save({ near_pct: v ?? 75 }), 5)}<span className="text-xs text-slate-500">%</span></span>
      </label>
      {!canEdit && <p className="text-xs text-slate-500">Only admins can change stage limits.</p>}
    </section>
  );
}
